import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, ChevronDown, ChevronRight, Minus, XCircle } from 'lucide-react';
import { Markdown } from '@/components/common/Markdown';
import type { AccountUser } from '@/lib/api/accounts';

interface AccountUsersTableProps {
  users: AccountUser[];
  isLoading: boolean;
  offset: number;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function IntentBadge({ intent }: { intent: string | null | undefined }) {
  if (intent === 'high') {
    return (
      <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400">
        High
      </Badge>
    );
  }
  if (intent === 'medium') {
    return (
      <Badge variant="outline" className="border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-400">
        Medium
      </Badge>
    );
  }
  if (intent === 'low') {
    return (
      <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
        Low
      </Badge>
    );
  }
  return <span className="text-muted-foreground text-sm">—</span>;
}

function QualBadge({ status }: { status: string | null | undefined }) {
  if (status === 'qualified') {
    return (
      <Badge variant="outline" className="gap-1 border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400">
        <CheckCircle2 className="h-3 w-3" />
        Qualified
      </Badge>
    );
  }
  if (status === 'unqualified') {
    return (
      <Badge variant="outline" className="gap-1 border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
        <XCircle className="h-3 w-3" />
        Unqualified
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="gap-1 border-gray-200 bg-gray-50 text-gray-500 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-400">
      <Minus className="h-3 w-3" />
      Unknown
    </Badge>
  );
}

const AccountUsersTable: React.FC<AccountUsersTableProps> = ({ users, isLoading, offset }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (email: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <p className="text-sm">No users for this account yet.</p>
      </div>
    );
  }

  return (
    <Table aria-label="Account users table">
      <TableCaption className="sr-only">
        Users belonging to this account, ordered by engagement.
      </TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12 text-center">#</TableHead>
          <TableHead className="w-8" />
          <TableHead>Person</TableHead>
          <TableHead>Last Seen</TableHead>
          <TableHead className="text-center">Sessions</TableHead>
          <TableHead>Qualification</TableHead>
          <TableHead>Intent</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {users.map((user, index) => {
          const isOpen = expanded.has(user.email);
          const Chevron = isOpen ? ChevronDown : ChevronRight;
          const handleRowClick = () =>
            navigate(`/people/${encodeURIComponent(user.email)}`);

          return (
            <React.Fragment key={user.email}>
              <TableRow
                className="cursor-pointer hover:bg-accent/50"
                onClick={handleRowClick}
              >
                <TableCell className="text-center text-muted-foreground text-sm">
                  {offset + index + 1}
                </TableCell>
                <TableCell className="w-8">
                  <button
                    type="button"
                    aria-label={isOpen ? 'Collapse summary' : 'Expand summary'}
                    onClick={(e) => toggle(user.email, e)}
                    className="rounded p-0.5 hover:bg-accent"
                  >
                    <Chevron className="h-4 w-4 text-muted-foreground" />
                  </button>
                </TableCell>
                <TableCell className="min-w-[220px]">
                  {user.name ? (
                    <div className="space-y-0.5">
                      <div className="font-medium text-heading">{user.name}</div>
                      <div className="text-sm text-muted-foreground">{user.email}</div>
                    </div>
                  ) : (
                    <div className="font-medium text-heading">{user.email}</div>
                  )}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {formatDate(user.last_seen_at)}
                </TableCell>
                <TableCell className="text-center text-sm">{user.total_sessions}</TableCell>
                <TableCell>
                  <QualBadge status={user.qualification_status} />
                </TableCell>
                <TableCell>
                  <IntentBadge intent={user.buying_intent} />
                </TableCell>
              </TableRow>
              {isOpen && (
                <TableRow key={`${user.email}-summary`} className="bg-muted/30">
                  <TableCell />
                  <TableCell />
                  <TableCell colSpan={5} className="py-3">
                    {user.summary_markdown ? (
                      <Markdown content={user.summary_markdown} className="text-sm max-w-2xl" />
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        No summary yet — generated automatically after each new session.
                      </p>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </React.Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default AccountUsersTable;
